/**
 * Utility functions for handling restaurant location status
 */

// Status reason constants matching backend
export const ClosureReason = {
  TEMPORARILY_CLOSED: "Temporarily Closed",
  UNDER_RENOVATION: "Under Renovation",
  PERMANENTLY_CLOSED: "Permanently Closed",
  RELOCATED: "Relocated",
  OPENING_SOON: "Opening Soon"
};

/**
 * Get badge configuration for a location's status
 * @param {object} location - Location object with is_closed, closure_reason, and expected_opening_date
 * @returns {object} Badge configuration with variant, text, and style properties
 */
export const getLocationStatusBadge = (location) => {
  if (!location) return null;

  // Handle "Opening Soon" status for new locations
  // Check both with is_closed=1 (not yet operational) or is_closed=0 (pre-opening)
  if (location.closure_reason === ClosureReason.OPENING_SOON ||
      (location.expected_opening_date && !location.closed_date && !location.is_closed)) {
    return {
      variant: "success",
      text: "OPENING SOON",
      bgColor: "#28a745",
      showDate: true,
      dateLabel: "Opens",
      opacity: 1.0
    };
  }

  // Handle closed locations
  if (location.is_closed) {
    switch (location.closure_reason) {
      case ClosureReason.UNDER_RENOVATION:
        return {
          variant: "warning",
          text: "UNDER RENOVATION",
          bgColor: "#ffc107",
          textColor: "#000",
          showDate: true,
          dateLabel: "Reopens",
          opacity: 0.95
        };

      case ClosureReason.RELOCATED:
        return {
          variant: "info",
          text: "RELOCATED",
          bgColor: "#17a2b8",
          showDate: false,
          opacity: 0.90
        };

      case ClosureReason.TEMPORARILY_CLOSED:
        return {
          variant: "secondary",
          text: "TEMPORARILY CLOSED",
          bgColor: "#6c757d",
          showDate: true,
          dateLabel: "Expected to reopen",
          opacity: 0.85
        };

      case ClosureReason.PERMANENTLY_CLOSED:
      default:
        return {
          variant: "danger",
          text: "CLOSED",
          bgColor: "#dc3545",
          showDate: false,
          opacity: 0.85
        };
    }
  }

  return null;
};

/**
 * Get background styling for a location card based on status
 * @param {object} location - Location object
 * @returns {object} Style object for the location card
 */
export const getLocationCardStyle = (location) => {
  const statusBadge = getLocationStatusBadge(location);

  if (!statusBadge) {
    return {
      backgroundColor: '#f8f9fa',
      borderColor: undefined,
      opacity: 1
    };
  }

  // Different background colors for different statuses
  const backgroundColors = {
    success: '#f0fff4', // Light green for opening soon
    warning: '#fff9e6', // Light yellow for renovation
    info: '#e7f6f8',    // Light blue for relocated
    secondary: '#f5f5f5', // Light gray for temp closed
    danger: '#fff5f5'   // Light red for permanently closed
  };

  const borderColors = {
    success: '#28a745',
    warning: '#ffc107',
    info: '#17a2b8',
    secondary: '#6c757d',
    danger: '#dc3545'
  };

  return {
    backgroundColor: backgroundColors[statusBadge.variant] || '#f8f9fa',
    borderColor: borderColors[statusBadge.variant],
    opacity: statusBadge.opacity
  };
};

/**
 * Format a date string for display
 * @param {string} dateString - ISO format date string
 * @returns {string} Formatted date string
 */
export const formatExpectedDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

/**
 * Format a closed date for display
 * @param {string} dateString - ISO format date string
 * @returns {string} Formatted date string
 */
export const formatClosedDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};
